/**
 * Generic Inventory Report Export Utility
 * Works for all inventory types: caps, bottles, labels, supplies
 */

import { supabase } from '../../../../supabaseClient';
import { exportChemicalInventoryReport } from './inventory-report-components/chemicalInventoryReport';
import { exportProductInventoryReport } from './inventory-report-components/productInventoryReport';

/**
 * Generate PDF styles for inventory reports
 */
const getReportStyles = () => {
  return `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Helvetica', 'Arial', sans-serif;
      padding: 20px;
      font-size: 10px;
      line-height: 1.4;
    }
    .header {
      text-align: center;
      margin-bottom: 20px;
      border-bottom: 2px solid #333;
      padding-bottom: 10px;
    }
    .company-name {
      font-size: 16px;
      font-weight: bold;
      color: #333;
    }
    .report-title {
      font-size: 14px;
      color: #666;
      margin-top: 2px;
    }
    .report-date {
      font-size: 8px;
      color: #999;
      margin-top: 2px;
    }
    .stats {
      display: flex;
      justify-content: space-around;
      margin: 15px 0;
      gap: 10px;
    }
    .stat-card {
      flex: 1;
      text-align: center;
      padding: 8px;
      border-radius: 4px;
      border: 1px solid #ddd;
    }
    .stat-card.total {
      background: #e3f2fd;
      border-color: #1976d2;
    }
    .stat-card.in-stock {
      background: #e8f5e9;
      border-color: #388e3c;
    }
    .stat-card.low-stock {
      background: #fff3e0;
      border-color: #f57c00;
    }
    .stat-card.out-stock {
      background: #ffebee;
      border-color: #d32f2f;
    }
    .stat-number {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 2px;
    }
    .stat-number.total { color: #1976d2; }
    .stat-number.in-stock { color: #388e3c; }
    .stat-number.low-stock { color: #f57c00; }
    .stat-number.out-stock { color: #d32f2f; }
    .stat-label {
      font-size: 8px;
      text-transform: uppercase;
      font-weight: bold;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9px;
      margin-top: 10px;
    }
    th, td {
      padding: 6px 8px;
      text-align: left;
      border-bottom: 1px solid #ddd;
      vertical-align: top;
    }
    th {
      background-color: #f0f0f0;
      font-weight: bold;
      font-size: 8px;
      text-transform: uppercase;
      text-align: center;
    }
    tr:nth-child(even) {
      background-color: #f9f9f9;
    }
    .item-name {
      font-weight: bold;
      font-size: 9px;
      margin-bottom: 1px;
    }
    .item-details {
      font-size: 7px;
      color: #666;
    }
    .quantity {
      font-weight: bold;
      text-align: center;
    }
    .center-align {
      text-align: center;
    }
    .status {
      padding: 1px 3px;
      border-radius: 2px;
      font-size: 7px;
      font-weight: bold;
      text-transform: uppercase;
      text-align: center;
      display: inline-block;
      min-width: 40px;
    }
    .status.in-stock {
      background: #d4edda;
      color: #155724;
    }
    .status.low-stock {
      background: #fff3cd;
      color: #856404;
    }
    .status.out-of-stock {
      background: #f8d7da;
      color: #721c24;
    }
    .lot-row {
      background-color: #f5f5f5 !important;
      font-size: 8px;
    }
    .lot-details {
      padding-left: 20px;
      font-size: 8px;
      color: #555;
    }
    @page {
      margin: 0;
      size: letter;
    }
    @media print {
      body { 
        margin: 0; 
        padding: 10px;
        font-size: 10px;
      }
      .stat-card {
        page-break-inside: avoid;
      }
      table {
        page-break-inside: auto;
      }
      tr {
        page-break-inside: avoid;
      }
    }
  `;
};

/**
 * Get the unit quantity for an item (handles chemicals vs regular)
 */
const getUnitQty = (item, isChemical) => {
  if (isChemical) return item.current_unit_quantity || 0;
  return item.total_quantity || item.current_quantity || 0;
};

/**
 * Get stock status for an item
 */
const getStockStatus = (item, isChemical) => {
  const qty = getUnitQty(item, isChemical);
  const rp = isChemical
    ? (item.reorder_point_units || 0)
    : (item.reorder_point || 0);

  if (rp > 0 && qty <= rp && qty > 0) {
    return { label: 'Low Stock', color: 'warning' };
  }
  if (qty === 0) {
    return { label: 'Out of Stock', color: 'error' };
  }
  return { label: 'In Stock', color: 'success' };
};

/**
 * Calculate inventory stats
 */
const calculateStats = (inventory, isChemical) => {
  const totalItems = inventory.length;
  const inStock = inventory.filter(item => getUnitQty(item, isChemical) > 0).length;
  const lowStock = inventory.filter(item => {
    const qty = getUnitQty(item, isChemical);
    const rp = isChemical ? (item.reorder_point_units || 0) : (item.reorder_point || 0);
    return rp > 0 && qty <= rp && qty > 0;
  }).length;
  const outOfStock = inventory.filter(item => getUnitQty(item, isChemical) <= 0).length;

  return { totalItems, inStock, lowStock, outOfStock };
};

/**
 * Format stock quantity display string
 * For chemicals: "5 drums / 275 gal"
 * For others: "120 units"
 */
const formatStockQty = (item, isChemical, unit) => {
  if (isChemical) {
    const unitType = item.unit_type || 'units';
    const unitMeasure = item.unit_measure || '';
    const unitQty = item.current_unit_quantity != null ? Number(item.current_unit_quantity) : 0;
    const totalQty = item.current_total_quantity != null ? Number(item.current_total_quantity) : 0;
    const unitStr = `${unitQty.toLocaleString()} ${unitType}`;
    return unitMeasure ? `${unitStr} / ${totalQty.toLocaleString()} ${unitMeasure}` : unitStr;
  }
  const qty = item.total_quantity || item.current_quantity || 0;
  return `${qty.toLocaleString()} ${unit}`;
};

/**
 * Format lot quantity display string for chemicals
 */
const formatLotQty = (lot, isChemical, unit, unitType, unitMeasure) => {
  if (isChemical) {
    const lotUnitQty = lot.current_unit_quantity != null ? Number(lot.current_unit_quantity) : 0;
    const lotTotalQty = lot.current_total_quantity != null ? Number(lot.current_total_quantity) : 0;
    const unitStr = `${lotUnitQty.toLocaleString()} ${unitType || 'units'}`;
    return unitMeasure ? `${unitStr} / ${lotTotalQty.toLocaleString()} ${unitMeasure}` : unitStr;
  }
  const qty = lot.current_quantity || 0;
  return `${qty.toLocaleString()} ${unit}`;
};

/**
 * Generate Summary Report HTML
 */
const generateSummaryReport = (companyName, inventory, config) => {
  const isChemical = !!config.hasUnitOfMeasure;
  const stats = calculateStats(inventory, isChemical);
  const itemTypeLabel = config.displayName;
  const itemTypeSingle = config.singularName || config.displayName.slice(0, -1);

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${itemTypeLabel} Inventory Report - Summary</title>
        <style>${getReportStyles()}</style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${companyName}</div>
          <div class="report-title">${itemTypeLabel} Inventory Report</div>
          <div class="report-date">Generated: ${new Date().toLocaleString()}</div>
        </div>

        <div class="stats">
          <div class="stat-card total">
            <div class="stat-number total">${stats.totalItems}</div>
            <div class="stat-label">Total ${itemTypeLabel}</div>
          </div>
          <div class="stat-card in-stock">
            <div class="stat-number in-stock">${stats.inStock}</div>
            <div class="stat-label">In Stock</div>
          </div>
          <div class="stat-card low-stock">
            <div class="stat-number low-stock">${stats.lowStock}</div>
            <div class="stat-label">Low Stock</div>
          </div>
          <div class="stat-card out-stock">
            <div class="stat-number out-stock">${stats.outOfStock}</div>
            <div class="stat-label">Out of Stock</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th width="30%">${itemTypeSingle}</th>
              <th width="15%">Location</th>
              <th width="${isChemical ? '20%' : '12%'}">Stock</th>
              <th width="12%">Reorder Pt</th>
              <th width="12%">Reorder Qty</th>
              <th width="${isChemical ? '11%' : '19%'}">Status</th>
            </tr>
          </thead>
          <tbody>
            ${inventory.map(item => {
              const status = getStockStatus(item, isChemical);
              const statusClass = status.label.toLowerCase().replace(' ', '-');
              const itemName = item.item_name || item[config.tableName]?.name || 'N/A';
              const itemSku = item.item_sku || item[config.tableName]?.sku || '';
              const locationName = item.location_name || item.location?.name || 'N/A';
              const unit = item[config.tableName]?.unit || config.displayName.toLowerCase();
              const stockDisplay = formatStockQty(item, isChemical, unit);
              const reorderPt = isChemical
                ? (item.reorder_point_units != null ? item.reorder_point_units : '-')
                : (item.reorder_point || '-');
              const reorderQty = isChemical
                ? (item.reorder_quantity_units != null ? item.reorder_quantity_units : '-')
                : (item.reorder_quantity ? item.reorder_quantity.toLocaleString() : '-');

              return `
                <tr>
                  <td>
                    <div class="item-name">${itemName}</div>
                    <div class="item-details">
                      ${itemSku ? `SKU: ${itemSku}` : ''}
                    </div>
                  </td>
                  <td class="center-align">${locationName}</td>
                  <td class="quantity">${stockDisplay}</td>
                  <td class="center-align">${reorderPt}</td>
                  <td class="center-align">${reorderQty}</td>
                  <td class="center-align">
                    <span class="status ${statusClass}">${status.label}</span>
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
    </html>
  `;
};

/**
 * Generate Detailed Report HTML with lot/batch breakdown
 */
const generateDetailedReport = (companyName, inventory, config) => {
  const isChemical = !!config.hasUnitOfMeasure;
  const stats = calculateStats(inventory, isChemical);
  const itemTypeLabel = config.displayName;
  const itemTypeSingle = config.singularName || config.displayName.slice(0, -1);

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${itemTypeLabel} Inventory Report - Detailed</title>
        <style>${getReportStyles()}</style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${companyName}</div>
          <div class="report-title">${itemTypeLabel} Inventory Report - Detailed</div>
          <div class="report-date">Generated: ${new Date().toLocaleString()}</div>
        </div>

        <div class="stats">
          <div class="stat-card total">
            <div class="stat-number total">${stats.totalItems}</div>
            <div class="stat-label">Total ${itemTypeSingle} Types</div>
          </div>
          <div class="stat-card in-stock">
            <div class="stat-number in-stock">${stats.inStock}</div>
            <div class="stat-label">In Stock</div>
          </div>
          <div class="stat-card low-stock">
            <div class="stat-number low-stock">${stats.lowStock}</div>
            <div class="stat-label">Low Stock</div>
          </div>
          <div class="stat-card out-stock">
            <div class="stat-number out-stock">${stats.outOfStock}</div>
            <div class="stat-label">Out of Stock</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th width="28%">${itemTypeSingle}</th>
              <th width="13%">Location</th>
              <th width="13%">Lot/Batch</th>
              <th width="${isChemical ? '22%' : '15%'}">Quantity</th>
              <th width="12%">Reorder Pt</th>
              <th width="${isChemical ? '12%' : '19%'}">Status</th>
            </tr>
          </thead>
          <tbody>
            ${inventory.map(item => {
              const lots = item.lots || [];
              const activeLots = isChemical
                ? lots.filter(lot => lot.is_active && (lot.current_unit_quantity || 0) > 0)
                : lots.filter(lot => lot.is_active && (lot.current_quantity || 0) > 0);
              const status = getStockStatus(item, isChemical);
              const statusClass = status.label.toLowerCase().replace(' ', '-');
              const itemName = item.item_name || item[config.tableName]?.name || 'N/A';
              const itemSku = item.item_sku || item[config.tableName]?.sku || '';
              const locationName = item.location_name || item.location?.name || 'N/A';
              const unit = item[config.tableName]?.unit || config.displayName.toLowerCase();
              const stockDisplay = formatStockQty(item, isChemical, unit);
              const unitType = item.unit_type || 'units';
              const unitMeasure = item.unit_measure || '';
              const reorderPt = isChemical
                ? (item.reorder_point_units != null ? item.reorder_point_units : '-')
                : (item.reorder_point || '-');

              if (activeLots.length === 0) {
                return `
                  <tr>
                    <td>
                      <div class="item-name">${itemName}</div>
                      <div class="item-details">${itemSku ? `SKU: ${itemSku}` : ''}</div>
                    </td>
                    <td class="center-align">${locationName}</td>
                    <td class="center-align">-</td>
                    <td class="quantity">${stockDisplay}</td>
                    <td class="center-align">${reorderPt}</td>
                    <td class="center-align">
                      <span class="status ${statusClass}">${status.label}</span>
                    </td>
                  </tr>
                `;
              }

              return `
                <tr>
                  <td>
                    <div class="item-name">${itemName}</div>
                    <div class="item-details">${itemSku ? `SKU: ${itemSku}` : ''}</div>
                  </td>
                  <td class="center-align">${locationName}</td>
                  <td class="center-align"><strong>TOTAL</strong></td>
                  <td class="quantity"><strong>${stockDisplay}</strong></td>
                  <td class="center-align">${reorderPt}</td>
                  <td class="center-align">
                    <span class="status ${statusClass}">${status.label}</span>
                  </td>
                </tr>
                ${activeLots.map(lot => `
                  <tr class="lot-row">
                    <td class="lot-details"></td>
                    <td class="center-align"></td>
                    <td class="center-align">
                      ${lot.lot_number ? `Lot: ${lot.lot_number}` : '-'}${lot.batch_number ? `<br/><small>Batch: ${lot.batch_number}</small>` : ''}
                    </td>
                    <td class="quantity">
                      ${formatLotQty(lot, isChemical, unit, unitType, unitMeasure)}
                    </td>
                    <td class="center-align"></td>
                    <td class="center-align"></td>
                  </tr>
                `).join('')}
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
    </html>
  `;
};

/**
 * Export inventory report as PDF
 * @param {string} inventoryType - Type of inventory (caps, bottles, labels, supplies)
 * @param {string} companyId - Company ID
 * @param {array} inventory - Inventory data (aggregated)
 * @param {object} config - Inventory configuration
 * @param {string} reportType - 'summary' or 'detailed'
 */
export const exportInventoryReport = async (inventoryType, companyId, inventory, config, reportType = 'summary') => {
  // Chemicals have their own dual-quantity report
  if (config?.hasUnitOfMeasure) {
    return exportChemicalInventoryReport(companyId, inventory, reportType);
  }

  // Products have their own cases+variant report
  if (inventoryType === 'products') {
    return exportProductInventoryReport(companyId, inventory, reportType);
  }

  try {
    // Fetch company name
    let companyName = 'Company Name';
    const { data: companyData, error } = await supabase
      .from('companies')
      .select('name')
      .eq('id', companyId)
      .single();

    if (!error && companyData) {
      companyName = companyData.name;
    }

    // Generate report based on type
    const printContent = reportType === 'detailed'
      ? generateDetailedReport(companyName, inventory, config)
      : generateSummaryReport(companyName, inventory, config);

    // Create iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(printContent);
    doc.close();
    
    // Wait a moment for content to load, then print
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      
      // Clean up after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  } catch (error) {
    console.error('Error exporting report:', error);
    throw error;
  }
};

/**
 * Export Report Dialog Component Props Interface
 * Use this in your inventory component:
 * 
 * const [exportReportDialogOpen, setExportReportDialogOpen] = useState(false);
 * const [reportType, setReportType] = useState('summary');
 * 
 * Then call: exportInventoryReport(inventoryType, companyId, filteredInventory, config, reportType)
 */
